/**
 * Reglas compartidas del semáforo (5 estados) entre dashboard, mapa y Supabase.
 *
 * - `v_parcelas_publicas` / `v_parcelas_tecnicas` calculan `estado` con firma → `contratada`.
 * - Fallback inventario JSON / filas derivadas deben usar la misma precedencia aquí.
 */

import type { EstadoGestion, TipoPropiedad } from './enums';
import { ESTADOS_GESTION } from './enums';
import type { InventarioAggregates, InventarioFile, InventarioRow, PoligonoCampo } from './inventario';
import { interesFromEstadoSemaforo } from './inventario';
import type { VParcelaPublica } from './db';

/** Presentación Tab Avance cliente (contratadas primero; mismas claves que FilterBar). */
export const ESTADOS_VISUAL_TABLER_CLIENTE: readonly EstadoGestion[] = [
  'contratada',
  'en_negociacion',
  'en_valoracion',
  'sin_prospectar',
  'inviable',
];

/** Orden pipeline / Mapbox / enumeración canónica (`ESTADOS_GESTION`). */
export function estadoKeysOrdered(): readonly EstadoGestion[] {
  return ESTADOS_GESTION;
}

export function isEstadoGestion(s: string): s is EstadoGestion {
  return (ESTADOS_GESTION as readonly string[]).includes(s);
}

/** Filtro del mapa: segmento UI o `'all'`. */
export type EstadoFilter = EstadoGestion | 'all';

/**
 * Parsea `data-sem-key` o similar. Clave desconocida → `'all'` (sin filtrar).
 */
export function estadoFromUserSegment(key: string): EstadoFilter {
  const k = key.trim();
  if (!k) return 'all';
  return isEstadoGestion(k) ? k : 'all';
}

/**
 * Estado efectivo para UI/mapa a partir de columnas crudas de `gestiones`.
 * Prioridad: `firmado` → `contratada`; si no, `estado` con default sin_prospectar.
 */
export function estadoEfectivoFromGestion(
  estado: EstadoGestion | null | undefined,
  firmado: boolean | null | undefined,
): EstadoGestion {
  if (firmado) return 'contratada';
  return estado ?? 'sin_prospectar';
}

export interface MergeParcelaLiveInput {
  parcela_id: string;
  /** Fila de `v_parcelas_publicas` si existe en API. */
  pub?: VParcelaPublica | null;
  /** Fallback inventario operativo (JSON o filas derivadas desde DB). */
  inventarioRow?: InventarioRow | null;
  /** Fecha base para bucket reunión (solo fallback inventario). */
  reunionRef?: Date;
}

/**
 * Precedencia: API pública (`pub`, ya con estado efectivo en vista) >
 * inventario Huimilpan (`inventarioRow`) > gris por defecto.
 *
 * `firmado` e `irregular` se toman de `pub` si hay fila; si no, se derivan del inventario.
 */
export function mergeParcelaLiveState(input: MergeParcelaLiveInput): {
  parcela_id: string;
  estado: EstadoGestion;
  firmado: boolean;
  irregular: boolean;
  reunion_bucket: 'semana' | 'mes' | 'sin_reunion';
} {
  const { parcela_id, pub, inventarioRow } = input;
  const ref = input.reunionRef ?? new Date();

  if (pub) {
    const estado = pub.firmado && pub.estado !== 'contratada' ? ('contratada' as const) : pub.estado;
    return {
      parcela_id,
      estado,
      firmado: pub.firmado,
      irregular: pub.irregular,
      reunion_bucket: 'sin_reunion',
    };
  }

  if (inventarioRow) {
    return {
      parcela_id,
      estado: inventarioRow.estado_semaforo as EstadoGestion,
      firmado: inventarioRow.estado_semaforo === 'contratada',
      irregular: inventarioRow.estado_semaforo === 'inviable',
      reunion_bucket: bucketReunion(inventarioRow.reunion_fecha, ref),
    };
  }

  return {
    parcela_id,
    estado: 'sin_prospectar',
    firmado: false,
    irregular: false,
    reunion_bucket: 'sin_reunion',
  };
}

function bucketReunion(
  fechaIso: string | null | undefined,
  ahora: Date,
): 'semana' | 'mes' | 'sin_reunion' {
  if (!fechaIso) return 'sin_reunion';
  const fecha = new Date(fechaIso);
  if (Number.isNaN(fecha.getTime())) return 'sin_reunion';
  const dias = (ahora.getTime() - fecha.getTime()) / (1000 * 60 * 60 * 24);
  if (dias < 0) return 'sin_reunion';
  if (dias <= 7) return 'semana';
  if (dias <= 30) return 'mes';
  return 'sin_reunion';
}



/** Aproxima polígono operativo desde RAN (`parcelas.tipo_propi`). */
export function poligonoFromTipoPropi(tipo: TipoPropiedad | null | undefined): InventarioRow['poligono'] {
  if (tipo === 'PROP_PRIV' || tipo === 'PROP_FED') return 'PROPIEDAD PRIVADA';
  return null;
}

/**
 * Réplica de `scripts/build_inventario_titulares.py` (agregados) sobre `InventarioRow[]`.
 * Usar con filas desde Supabase o JSON para una sola fuente de KPIs.
 */
export function computeInventarioAggregates(rows: InventarioRow[]): InventarioAggregates {
  const total = rows.length;

  const count = (pred: (r: InventarioRow) => boolean): number => rows.reduce((n, r) => n + (pred(r) ? 1 : 0), 0);

  const zonasSet = new Set<number>();
  for (const r of rows) {
    if (r.zona != null) zonasSet.add(r.zona);
  }
  const por_zona: Record<string, number> = {};
  for (const z of [...zonasSet].sort((a, b) => a - b)) {
    por_zona[`Z${z}`] = count((r) => r.zona === z);
  }

  return {
    total,
    por_poligono: {
      P1: count((r) => r.poligono === 1),
      P2: count((r) => r.poligono === 2),
      PP: count((r) => typeof r.poligono === 'string'),
    },
    por_zona,
    por_interes: {
      si: count((r) => r.interes === 'si'),
      pd: count((r) => r.interes === 'pd'),
      sin_tocar: count((r) => r.interes === 'sin_tocar'),
    },
    por_semaforo: {
      sin_prospectar: count((r) => r.estado_semaforo === 'sin_prospectar'),
      en_valoracion: count((r) => r.estado_semaforo === 'en_valoracion'),
      en_negociacion: count((r) => r.estado_semaforo === 'en_negociacion'),
      contratada: count((r) => r.estado_semaforo === 'contratada'),
      inviable: count((r) => r.estado_semaforo === 'inviable'),
    },
    con_reunion: count((r) => r.reunion_fecha != null),
    sin_reunion: count((r) => r.reunion_fecha == null),
    con_telefono: count((r) => r.tiene_telefono),
    sin_telefono: count((r) => !r.tiene_telefono),
    area_total_ha: round2(rows.reduce((s, r) => s + (r.area_ha ?? 0), 0)),
    area_si_ha: round2(
      rows.filter((r) => r.estado_semaforo === 'en_negociacion').reduce((s, r) => s + (r.area_ha ?? 0), 0),
    ),
    area_pd_ha: round2(
      rows.filter((r) => r.estado_semaforo === 'en_valoracion').reduce((s, r) => s + (r.area_ha ?? 0), 0),
    ),
    area_contratada_ha: round2(
      rows.filter((r) => r.estado_semaforo === 'contratada').reduce((s, r) => s + (r.area_ha ?? 0), 0),
    ),
    area_inviable_ha: round2(
      rows.filter((r) => r.estado_semaforo === 'inviable').reduce((s, r) => s + (r.area_ha ?? 0), 0),
    ),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Snapshot del estado vivo + metadata de una parcela. Se usa para alimentar
 *  `inventarioFileWithLiveEstado` con datos suficientes para:
 *    - Overrides de filas existentes (solo `estado` se aplica).
 *    - Construcción de filas sintéticas cuando una parcela vive en el mapa
 *      pero no tiene fila en `inventario_titulares` — los demás campos
 *      (area_ha, num_parcela, poligono, zona, tipo_parcela) se copian a la
 *      fila sintética para que el listado del Tab Inventario y los KPIs
 *      por superficie las muestren con metadata real (no "Parcela —").
 *  Todos los metadatos son opcionales — si null/undefined, la fila sintética
 *  los deja en null y la UI cae a sus placeholders. */
export interface LiveSnapshot {
  estado: EstadoGestion;
  area_ha?: number | null;
  /** Número de parcela como string (HUM trae "42", COR puede traer "S/N"). */
  num_parcela?: string | null;
  /** Versión int del num_parcela cuando es numérico (para sort estable). */
  num_parcela_int?: number | null;
  /** PoligonoCampo: 1, 2, 'PROPIEDAD PRIVADA' o null. */
  poligono?: PoligonoCampo | null;
  /** Zona del polígono (Z1, Z2, ...). */
  zona?: number | null;
  /** Tipo de parcela ("Ejido", "Pequeña propiedad", etc). */
  tipo_parcela?: string | null;
}

export interface InventarioFileWithLiveEstadoOptions {
  /**
   * Resolver fallback row→parcela_id. Si `row.parcela_id` viene null/empty
   * (por `v_inventario.parcela_codigo` null cuando el FK está roto en
   * `inventario_titulares`), el helper llama este resolver para intentar
   * derivar el id por num_parcela + área (ver
   * `apps/dashboard/src/hooks/useParcelasLiveMerged.ts:resolveParcelaIdFromInventarioRow`).
   */
  resolveParcelaId?: (row: InventarioRow) => string | undefined;
}

/**
 * Inventario + agregados alineados con el estado efectivo del mapa (`ParcelaLiveState` /
 * propiedad `live_estado` en GeoJSON), vía `parcela_id`. Tres mecanismos para no
 * dejar parcelas contratadas fuera del KPI cuando `v_inventario` va rezagado:
 *
 *   1. **Override** de filas existentes cuyo `parcela_id` (código público
 *      HUM-Pxxxx) coincide con una entrada del mapa.
 *   2. **Resolver fallback** opcional para filas con `parcela_id` null (FK roto
 *      en `inventario_titulares`) — usa num_parcela + área para resolver.
 *   3. **Filas sintéticas** para parcelas en el mapa que NO tienen fila en
 *      `inventario_titulares` (típico: parcelas con `gestiones.estado='contratada'`
 *      pero sin propietario seedeado). Se construye una fila mínima con el
 *      area_ha del snapshot para que cuente en `por_superficie`.
 *
 * El resultado mantiene `inv.rows` original + override + sintéticas, y recomputa
 * `aggregates` con `computeInventarioAggregates` sobre el conjunto unión.
 */
export function inventarioFileWithLiveEstado(
  inv: InventarioFile,
  liveByParcelaId: ReadonlyMap<string, LiveSnapshot>,
  options: InventarioFileWithLiveEstadoOptions = {},
): InventarioFile {
  if (liveByParcelaId.size === 0) return inv;

  let changed = false;
  const matched = new Set<string>();

  // Pass 1 — override estado_semaforo en filas existentes.
  const rows = inv.rows.map((r) => {
    let pid = r.parcela_id?.trim() ?? '';
    if (!pid && options.resolveParcelaId) {
      const resolved = options.resolveParcelaId(r);
      if (resolved) pid = resolved.trim();
    }
    if (!pid) return r;
    const live = liveByParcelaId.get(pid);
    if (!live) return r;
    matched.add(pid);
    if (live.estado === r.estado_semaforo) return r;
    changed = true;
    return {
      ...r,
      // Si el resolver llenó pid, también lo persistimos en la fila para que
      // próximos pases por la misma fila no vuelvan a llamar al resolver.
      parcela_id: r.parcela_id ?? pid,
      estado_semaforo: live.estado,
      interes: interesFromEstadoSemaforo(live.estado),
    };
  });

  // Pass 2 — filas sintéticas para parcelas del mapa sin fila en inventario.
  const synthetic: InventarioRow[] = [];
  for (const [pid, live] of liveByParcelaId) {
    if (matched.has(pid)) continue;
    synthetic.push(makeSyntheticInventarioRow(pid, live));
    changed = true;
  }

  if (!changed) return inv;

  const merged = synthetic.length === 0 ? rows : rows.concat(synthetic);
  return {
    ...inv,
    rows: merged,
    aggregates: computeInventarioAggregates(merged),
  };
}

/** Construye una fila mínima de `InventarioRow` con sólo el código de parcela,
 *  estado y área. Resto de campos null/defaults — la fila NO es para mostrar
 *  en el listado del inventario, solo para que `computeInventarioAggregates`
 *  la cuente en `por_semaforo` y `por_superficie`. */
function makeSyntheticInventarioRow(parcela_id: string, live: LiveSnapshot): InventarioRow {
  const area = typeof live.area_ha === 'number' && Number.isFinite(live.area_ha) ? live.area_ha : 0;
  return {
    poligono: live.poligono ?? null,
    zona: live.zona ?? null,
    num_parcela: live.num_parcela ?? null,
    num_parcela_int: live.num_parcela_int ?? null,
    tipo_parcela: live.tipo_parcela ?? null,
    folio_tierras: null,
    folio_derechos: null,
    clave_catastral: null,
    area_ha: area,
    reunion_fecha: null,
    dias_desde_reunion: null,
    titular: null,
    titular_masked: null,
    telefono: null,
    tiene_telefono: false,
    propietario_id: null,
    parcela_id,
    gestion_updated_at: null,
    interes: interesFromEstadoSemaforo(live.estado),
    estado_semaforo: live.estado,
    expediente_completitud_pct: null,
  };
}
